import * as React from "react"
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { Plus, ChevronRight, ChevronDown, Users, Tag, Pin, PinOff } from "lucide-react"
import { useGroupCategories } from "@/hooks/queries/useCategories"
import { useGroupUsers } from "@/hooks/queries/useGroups"
import type { Group, Category } from "@/types/domain.types"

interface GroupCategoriesSectionProps {
  group: Group
  isExpanded: boolean
  onToggleExpand: () => void
  onCreateCategory: (groupId: string) => void
  onEditCategory: (category: Category, groupId: string) => void
  onDeleteCategory: (categoryId: string, groupId: string) => void
  onTogglePinCategory: (categoryId: string, isPinned: boolean, groupId: string) => void
}

export const GroupCategoriesSection: React.FC<GroupCategoriesSectionProps> = ({
  group,
  isExpanded,
  onToggleExpand,
  onCreateCategory,
  onEditCategory,
  onDeleteCategory: _onDeleteCategory,
  onTogglePinCategory,
}) => {
  const { data: categories = [], isLoading: isLoadingCategories } = useGroupCategories(
    group.id
  )
  const { data: groupMembers = [], isLoading: isLoadingMembers } = useGroupUsers(
    group.id
  )

  const isLoading = isLoadingCategories || isLoadingMembers

  return (
    <Card className="overflow-hidden">
      <CardHeader
        className="cursor-pointer hover:bg-accent/50 transition-colors"
        onClick={onToggleExpand}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-6 w-6">
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </Button>
            <div className="flex items-center gap-3">
              {group.emoji ? (
                <span className="text-2xl">{group.emoji}</span>
              ) : (
                <div className="h-8 w-8 rounded bg-primary/10 flex items-center justify-center">
                  <Tag className="h-4 w-4 text-primary" />
                </div>
              )}
              <div>
                <CardTitle className="text-lg">{group.name}</CardTitle>
                <CardDescription className="flex items-center gap-4 mt-1">
                  <span className="flex items-center gap-1">
                    <Users className="h-3 w-3" />
                    {group.members?.length || 0} members
                  </span>
                  <span className="flex items-center gap-1">
                    <Tag className="h-3 w-3" />
                    {categories.length} categories
                  </span>
                </CardDescription>
              </div>
            </div>
          </div>
          <Button
            size="sm"
            onClick={(e) => {
              e.stopPropagation()
              onCreateCategory(group.id)
            }}
          >
            <Plus className="h-4 w-4 mr-1" />
            New Category
          </Button>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent className="pt-0">
          {isLoading ? (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : categories.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              <Tag className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm font-medium mb-1">No categories yet</p>
              <p className="text-xs">Create your first category to start organizing expenses</p>
            </div>
          ) : (
            <div className="grid gap-2">
              {categories.map((category) => {
                const isPinned = category.pinned === true
                // TODO: Add default_splits when backend supports it
                const defaultSplits: any[] = []

                return (
                  <div
                    key={category.id}
                    className="p-3 rounded-lg border hover:bg-accent/50 transition-colors cursor-pointer"
                    onClick={() => onEditCategory(category, group.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {category.emoji ? (
                          <span className="text-xl">{category.emoji}</span>
                        ) : (
                          <div className="h-8 w-8 rounded bg-secondary flex items-center justify-center">
                            <Tag className="h-4 w-4 text-muted-foreground" />
                          </div>
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{category.name}</span>
                            {isPinned && (
                              <Badge variant="outline" className="text-xs">
                                Pinned
                              </Badge>
                            )}
                          </div>
                          {/* TODO: Add description when backend supports it */}
                          {defaultSplits.length > 0 && (
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-muted-foreground">
                                Default split:
                              </span>
                              {defaultSplits.slice(0, 3).map((split, index) => {
                                const member = groupMembers.find(m => m.id === split.user_id)
                                return (
                                  <span key={split.user_id} className="text-xs">
                                    {member?.name || 'Unknown'}: {split.weight}
                                    {index < Math.min(defaultSplits.length - 1, 2) && ','}
                                  </span>
                                )
                              })}
                              {defaultSplits.length > 3 && (
                                <span className="text-xs text-muted-foreground">
                                  +{defaultSplits.length - 3} more
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={(e) => {
                            e.stopPropagation()
                            onTogglePinCategory(category.id, !isPinned, group.id)
                          }}
                          title={isPinned ? "Unpin category" : "Pin category"}
                        >
                          {isPinned ? (
                            <Pin className="h-3 w-3 text-primary" />
                          ) : (
                            <PinOff className="h-3 w-3" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  )
}